// Players with accounts (migration 0052): an invite link, a seat in a campaign, and the character that is theirs.
//
// ⚠⚠ A SEAT IS A WAY TO FIND THE LINKS, NOT A SECOND SET OF DOORS. A signed-in player opens the same /t, /c and /p pages a
// phone with a link opens; `my_play()` only tells them which tokens are theirs. So nothing a player sees signed in is
// something the GM hadn't already put behind a link — see the migration's header before adding a direct read here.
import { supabase } from "./supabase";
import { writeOneOrSay } from "./write-or-say";
import type { Character, CharacterSummary } from "./characters";

export const PLAY_PATH = "/play";
/** A player's own characters: the Character Generator, and sending one to the GM (0054). */
export const PLAY_CHARACTERS_PATH = "/play/characters";
export const JOIN_PREFIX = "/join/";
/** Added to a link opened from the player's home, so that page can offer the way back. */
export const SEAT_PARAM = "seat";

/** The invite token in `/join/<token>`, or null. */
export function joinToken(pathname: string): string | null {
  const m = /^\/join\/([A-Za-z0-9_-]{8,})\/?$/.exec(pathname);
  return m ? m[1]! : null;
}

export const inviteUrl = (origin: string, token: string) => `${origin}${JOIN_PREFIX}${token}`;
export const seatHref = (path: string) => `${path}?${SEAT_PARAM}=1`;
export const cameFromSeat = (search: string) => new URLSearchParams(search).get(SEAT_PARAM) === "1";

// ── The invite a visitor is holding, across the trip through "check your email" ──────────────────────────────────
//
// ⚠ AN ACCOUNT MADE FROM AN INVITE COMES BACK THROUGH /welcome, NOT THROUGH THE INVITE. The emailed link can't carry the
// token (it goes to one fixed page), so the token waits here and the app returns to the invite once someone is signed in.
const PENDING_KEY = "compendium.pendingInvite";

export function rememberInvite(token: string): void {
  try { localStorage.setItem(PENDING_KEY, token); } catch { /* private window: the invite link still works when reopened */ }
}
export function pendingInvite(): string | null {
  try { return localStorage.getItem(PENDING_KEY); } catch { return null; }
}
export function forgetInvite(): void {
  try { localStorage.removeItem(PENDING_KEY); } catch { /* see rememberInvite */ }
}

// ── What a player reads ──────────────────────────────────────────────────────────────────────────────────────────
export type SeatCharacter = {
  id: string; name: string; portrait: string | null; /** 0099: the GM's framing (0–100% down), when they set one. */ portrait_focus?: number | null; summary: CharacterSummary | null; player_name: string;
  /** Only on the invite page: someone else's already. */
  taken?: boolean;
  mine: boolean;
  /** The sheet link, on the player's own characters only. */
  token?: string | null;
  /** Since 0102 the same id as the character: the builder opens the row itself. Kept for the screens that read it. */
  source_id?: string | null;
  /** 0103: lent to me by its player or the GM — whose it really is, for the "standing in for" label. */
  standing_in?: string | null;
  /** 0103: on a character that is MINE — who I (or the GM) have lent it to, and until when. */
  lent?: { id: string; user_id: string; name: string; until: string | null }[];
};
export type SeatTable = { token: string; scene_id: string; scene_name: string; shared_at: string };
export type Seat = {
  id: string; name: string; blurb: string; gm: string; joined_at: string;
  campaign_token: string | null;
  /**
   * 0155: the table's voice or video link and its short name, for the "Join the call" button (home card, table rail).
   * ⚠ OPTIONAL ON READ: absent until `my_play` is restated to send them; a session held open across that deploy gets neither.
   */
  call_url?: string | null;
  call_label?: string | null;
  tables: SeatTable[];
  /** Mine first, then the ones nobody has claimed yet. */
  characters: SeatCharacter[];
  /** Characters this player built and sent the GM, newest first (0054). */
  submissions?: SeatSubmission[];
};
export type MyPlay = { owns_campaigns: boolean; campaigns: Seat[] };

export type InvitePreview = {
  campaign: { id: string; name: string; blurb: string };
  gm: string;
  joined: boolean;
  /** The GM opened their own invite (0053). */
  is_gm?: boolean;
  /** Null until someone is signed in. */
  characters: SeatCharacter[] | null;
};

export async function invitePreview(token: string): Promise<InvitePreview | null | "error"> {
  const { data, error } = await supabase.rpc("invite_preview", { p_token: token });
  if (error) return "error";
  return (data ?? null) as InvitePreview | null;
}

type Outcome = { ok: boolean; error?: string; campaign_id?: string };

export async function joinCampaign(token: string, characterId: string | null): Promise<Outcome> {
  const { data, error } = await supabase.rpc("join_campaign", { p_token: token, p_character_id: characterId });
  if (error) return { ok: false, error: error.message };
  return (data ?? { ok: false, error: "No answer came back." }) as Outcome;
}

export async function claimCharacter(characterId: string): Promise<Outcome> {
  const { data, error } = await supabase.rpc("claim_character", { p_character_id: characterId });
  if (error) return { ok: false, error: error.message };
  return (data ?? { ok: false, error: "No answer came back." }) as Outcome;
}

/**
 * GM or player, remembered per account on this device so the next load opens the right page at once (see app.tsx).
 * "gm+player" runs campaigns AND sits at someone's table: the workspace, with the way to /play in the account menu.
 */
export type Role = "gm" | "gm+player" | "player";
const ROLE_KEY = (userId: string) => `compendium.role.${userId}`;
/**
 * ⚠⚠ ONLY AN ACCOUNT THAT OWNS A CAMPAIGN IS A GM. A new account used to be treated as a GM because it sat at no table,
 * and opened on the (empty) workspace — a player who signed up and then typed the site's address saw "the GM side"
 * (2026-09-14). Now everyone else lands on the player home, which offers "Run a campaign of your own" on purpose.
 */
export const roleOf = (d: MyPlay): Role => (d.owns_campaigns ? (d.campaigns.length ? "gm+player" : "gm") : "player");
export function rememberedRole(userId: string): Role | null {
  try { return (localStorage.getItem(ROLE_KEY(userId)) as Role | null) ?? null; } catch { return null; }
}
export function rememberRole(userId: string, role: Role): void {
  try { localStorage.setItem(ROLE_KEY(userId), role); } catch { /* remembered next time, or not */ }
}

/** Null on a failed read — never mistaken for "sits nowhere". */
export async function myPlay(): Promise<MyPlay | null> {
  const { data, error } = await supabase.rpc("my_play");
  if (error || !data) return null;
  return data as MyPlay;
}

/** A player's own seats, for a leave button. */
export async function leaveCampaign(campaignId: string, userId: string, say: (m: string) => void): Promise<boolean> {
  return !!(await writeOneOrSay("leave that campaign",
    () => supabase.from("campaign_members").delete().eq("campaign_id", campaignId).eq("user_id", userId).select("user_id"), say));
}

// ── What the GM manages ──────────────────────────────────────────────────────────────────────────────────────────
export type Member = { user_id: string; display_name: string; email: string; joined_at: string };

export async function listMembers(campaignId: string, say: (m: string) => void): Promise<Member[] | null> {
  const { data, error } = await supabase.from("campaign_members")
    .select("user_id,display_name,email,joined_at").eq("campaign_id", campaignId).order("joined_at");
  if (error) { say(`Couldn't load your players — ${error.message}`); return null; }
  return (data ?? []) as Member[];
}

export async function removeMember(campaignId: string, userId: string, say: (m: string) => void): Promise<boolean> {
  return !!(await writeOneOrSay("take that player's seat away",
    () => supabase.from("campaign_members").delete().eq("campaign_id", campaignId).eq("user_id", userId).select("user_id"), say));
}

/** The live invite token, or null when there is none. Undefined on a failed read. */
export async function liveInvite(campaignId: string, say: (m: string) => void): Promise<string | null | undefined> {
  const { data, error } = await supabase.from("share_links").select("token")
    .eq("campaign_id", campaignId).eq("mode", "invite").is("revoked_at", null)
    .order("created_at", { ascending: false }).limit(1);
  if (error) { say(`Couldn't load the invite link — ${error.message}`); return undefined; }
  return (data?.[0] as { token: string } | undefined)?.token ?? null;
}

export async function createInvite(campaignId: string, ownerId: string, say: (m: string) => void): Promise<string | null> {
  const row = await writeOneOrSay<{ token: string }>("make an invite link",
    () => supabase.from("share_links").insert({ campaign_id: campaignId, owner_id: ownerId, mode: "invite" }).select("token"), say);
  return row?.token ?? null;
}

/** Which characters in the campaign belong to which player (GM side). */
export async function setCharacterPlayer(characterId: string, userId: string | null, say: (m: string) => void): Promise<boolean> {
  return !!(await writeOneOrSay(userId ? "give that character to the player" : "take that character back",
    () => supabase.from("characters").update({ player_user_id: userId }).eq("id", characterId).select("id"), say));
}

// ── Characters a player builds and sends to the GM (0054) ────────────────────────────────────────────────────────
export type SubmissionStatus = "pending" | "accepted" | "declined";
/** The player's own view of one they sent. */
export type SeatSubmission = { id: string; character_id: string; character_name: string; status: SubmissionStatus; gm_note: string; created_at: string };
/** The GM's view: what is waiting, and what was decided. */
export type Submission = {
  id: string; status: SubmissionStatus; note: string; gm_note: string; created_at: string; decided_at: string | null;
  accepted_character_id: string | null; player: string; player_user_id: string;
  /** A character this player sent before and the GM accepted: accepting again updates that copy. */
  updates: boolean;
  character: { name: string; portrait: string | null; summary: CharacterSummary | null };
};

export async function submitCharacter(characterId: string, campaignId: string, note: string): Promise<Outcome> {
  const { data, error } = await supabase.rpc("submit_character", { p_character_id: characterId, p_campaign_id: campaignId, p_note: note });
  if (error) return { ok: false, error: error.message };
  return (data ?? { ok: false, error: "No answer came back." }) as Outcome;
}

export async function withdrawSubmission(id: string, say: (m: string) => void): Promise<boolean> {
  return !!(await writeOneOrSay("take that back",
    () => supabase.from("character_submissions").delete().eq("id", id).eq("status", "pending").select("id"), say));
}

/** Null on a failed read. */
/**
 * Every campaign of this GM's that has a character waiting — the rows are the GM's to read (policy submitter_or_gm_read),
 * and a player's own pending rows are dropped by `notMine`. Null when the read failed (say nothing; try again).
 */
export async function campaignsWithWaiting(userId: string): Promise<string[] | null> {
  const { data, error } = await supabase.from("character_submissions").select("campaign_id,submitted_by").eq("status", "pending");
  if (error || !data) return null;
  const rows = data as { campaign_id: string; submitted_by: string | null }[];
  return [...new Set(rows.filter((r) => r.submitted_by !== userId).map((r) => r.campaign_id))];
}

export async function campaignSubmissions(campaignId: string): Promise<Submission[] | null> {
  const { data, error } = await supabase.rpc("campaign_submissions", { p_campaign_id: campaignId });
  if (error || !data) return null;
  return data as Submission[];
}

export async function acceptSubmission(id: string): Promise<Outcome & { character_id?: string; updated?: boolean }> {
  const { data, error } = await supabase.rpc("accept_submission", { p_id: id });
  if (error) return { ok: false, error: error.message };
  return (data ?? { ok: false, error: "No answer came back." }) as Outcome;
}

export async function declineSubmission(id: string, note: string): Promise<Outcome> {
  const { data, error } = await supabase.rpc("decline_submission", { p_id: id, p_note: note });
  if (error) return { ok: false, error: error.message };
  return (data ?? { ok: false, error: "No answer came back." }) as Outcome;
}

/** The whole submitted character, shaped like a `characters` row, for the GM's review (0055). Null when it can't be read. */
export async function submissionCharacter(id: string): Promise<Character | null> {
  const { data, error } = await supabase.rpc("submission_character", { p_id: id });
  if (error || !data) return null;
  return data as Character;
}

/**
 * Open the full Character Generator on a party character, as the player.
 *
 * ⚠ ONE SHEET (migration 0102, the owner: "there should only be one gold standard sheet to work from"). This used to
 * find or MAKE the player's own copy of the party's row and open that, with "Send the update" carrying edits back —
 * which is how a sheet said 750 gp while the shop said 0. The party's row IS the character now, the player edits it
 * under `player_edits_own`, and this just opens it. `knownSource` is kept for the callers that still pass it: since
 * 0102 it is the same id.
 */
export async function builderForMyCharacter(partyCharacterId: string, knownSource?: string | null): Promise<{ href: string } | { error: string }> {
  return { href: `${PLAY_CHARACTERS_PATH}?char=${knownSource || partyCharacterId}` };
}
